import React from "react";
import PropTypes from "prop-types";
import { Paper, Grid, Box, Typography, Stack } from "@mui/material";
import { colors, customShadows, borderRadius, borderRadiusPx } from "../../../../theme";

/**
 * BottomMetricsRibbon - Full-width dark navy stats ribbon displaying platform/network metrics.
 */
const BottomMetricsRibbon = ({
  metrics = [],
  sx = {},
}) => {
  const r = colors.ribbon;

  if (!metrics || metrics.length === 0) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: borderRadiusPx.ribbon,
        backgroundColor: r.bg,
        border: `1px solid ${r.border}`,
        p: { xs: 2.5, md: 3 },
        color: r.value,
        boxShadow: customShadows.ribbon,
        ...sx,
      }}
    >
      <Grid container spacing={{ xs: 2, md: 2 }} alignItems="center">
        {metrics.map((item, idx) => (
          <Grid
            item
            xs={6}
            sm={4}
            md={12 / metrics.length}
            key={idx}
            sx={{
              borderRight: {
                md: idx !== metrics.length - 1 ? `1px solid ${r.divider}` : "none",
              },
              pr: { md: 2 },
            }}
          >
            <Box>
              <Typography
                variant="caption"
                sx={{
                  color: r.label,
                  fontWeight: 500,
                  fontSize: "0.74rem",
                  display: "block",
                  mb: 0.5,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {item.label}
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: "1.1rem", md: "1.25rem" },
                  color: r.value,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.2,
                  mb: 0.5,
                }}
              >
                {item.value}
              </Typography>

              {item.delta && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      fontSize: "0.72rem",
                      color: item.isPositive !== false ? r.deltaPositive : r.deltaNegative,
                    }}
                  >
                    {item.isPositive !== false ? "▲" : "▼"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 500,
                      fontSize: "0.72rem",
                      color: item.isPositive !== false ? r.deltaPositiveSub : r.deltaNegativeSub,
                    }}
                  >
                    {item.delta}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

BottomMetricsRibbon.propTypes = {
  metrics: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      delta: PropTypes.string,
      isPositive: PropTypes.bool,
    })
  ),
  sx: PropTypes.object,
};

export default BottomMetricsRibbon;
